import { useEffect, useState } from 'react';
import { getQuestionTrends, type QuestionTrend } from '../account';
import StateDots from './StateDots';

/**
 * Every question, per engine, over time (D-34).
 *
 * A run-level "2/25 named" cannot tell you WHICH two, so it cannot tell you that
 * a question you won last month is gone, or that the page you published finally
 * landed. Questions that have ever won come first — those are the ones worth
 * defending, and the ones worth writing more like.
 */
export default function QuestionTrends({ filter }: { filter: { domain?: string; engine?: string; from?: string; to?: string; querySet?: string } }): JSX.Element | null {
  const [data, setData] = useState<{ runs: number; questions: QuestionTrend[] } | null>(null);
  const [all, setAll] = useState(false);

  /*
    `engine` was missing from both the prop type and this dependency list, so
    picking one engine left the other in the "Latest" column and nothing
    refetched. It reached the server anyway, because the caller passes a wider
    object than the type admits and the query string is built by iterating it,
    which is the sort of accident that hides a bug rather than causing one.
  */
  useEffect(() => {
    getQuestionTrends(filter).then(setData).catch(() => setData(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter.domain, filter.engine, filter.from, filter.to, filter.querySet]);

  if (!data || data.questions.length === 0) return null;
  const winners = data.questions.filter((q) => q.wins > 0);
  const shown = all ? data.questions : data.questions.slice(0, 12);
  // With one engine filtered in, a legend explaining how to tell two apart is
  // an instruction for a distinction that is not on screen.
  const engines = new Set(data.questions.flatMap((q) => q.points.map((p) => p.engine)));

  return (
    <div className="mb-5 border border-warm-dark-2 bg-warm-dark-2/20 rounded-xl p-4">
      <h3 className="font-serif text-lg mb-1">Question by question</h3>
      <p className="text-sm text-text-secondary mb-3">
        Each question across {data.runs} {data.runs === 1 ? 'run' : 'runs'}, per engine, oldest on the left.
        {winners.length > 0
          ? <> <span className="text-success">{winners.length}</span> {winners.length === 1 ? 'question has' : 'questions have'} earned you a mention. Those are the ones to defend, and to write more like.</>
          : ' None has earned a mention yet.'}
      </p>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-text-secondary font-mono text-xs text-left">
            <th className="py-1 pr-3">Question</th>
            {/* The same wording often lives in more than one set, so a row saying
                "1 of 4" was quietly mixing measurements and the table gave no way
                to tell which one it belonged to. */}
            <th className="pr-3">Question set</th>
            <th className="pr-3">Won</th><th className="pr-3">History</th><th>Latest</th>
          </tr></thead>
          <tbody>
            {shown.map((q) => (
              <tr key={q.query} className="border-t border-warm-dark-2 align-top">
                <td className="py-2 pr-3 text-text-primary">{q.query}</td>
                <td className="pr-3 text-text-secondary text-xs max-w-[12rem]" title={q.sets.join(', ')}>
                  {q.sets.length ? q.sets.join(', ') : <span className="text-text-secondary/50">unnamed</span>}
                </td>
                <td className="pr-3 font-mono whitespace-nowrap">
                  <span className={q.wins > 0 ? 'text-success' : 'text-text-secondary'}>{q.wins}</span>
                  <span className="text-text-secondary/60">/{q.asked}</span>
                </td>
                <td className="pr-3">
                  <StateDots points={q.points} />
                </td>
                <td className="font-mono text-xs whitespace-nowrap">
                  {Object.entries(q.latest).map(([engine, state]) => (
                    <span key={engine} className="mr-2">
                      <span className="text-text-secondary/70">{engine === 'gemini' ? 'Gemini' : 'GPT'} </span>
                      <span className={state === 'mentioned' ? 'text-success' : state === 'cited_only' ? 'text-estimated' : 'text-text-secondary'}>
                        {state === 'mentioned' ? 'named' : state === 'cited_only' ? 'cited' : 'absent'}
                      </span>
                    </span>
                  ))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {data.questions.length > 12 && (
        <button onClick={() => setAll((v) => !v)} className="font-mono text-sm text-cyan hover:underline mt-2">
          {all ? 'show fewer' : `show all ${data.questions.length} questions`}
        </button>
      )}
      <p className="font-mono text-xs text-text-secondary/70 mt-2">
        {engines.size > 1 && <>A ring around a dot means GPT; plain is Gemini. </>}
        Questions the engine couldn&apos;t answer are left out, because an outage is not evidence either way.
      </p>
    </div>
  );
}
